import {GaiaRed} from '@gaia/colors';
import {useLatest} from '@gaia/hooks/useLatest';
import theme from '@gaia/theme';
import {isGaiaColorHex, isMarkerType} from '@gaia/utils/typing';
import {Marker} from 'mapbox-gl';
import {useCallback, useEffect, useRef, useState} from 'react';

import WaypointIcon from './WaypointIcon';

interface DraggableWaypointProps {
    map: mapboxgl.Map | undefined;
    interaction: InteractionState;
    onInteractionChange: SetState<InteractionState>;
}

export default function DraggableWaypoint({
    map,
    interaction,
    onInteractionChange,
}: DraggableWaypointProps) {
    const markerRef = useRef<HTMLDivElement>(null);

    const [draggableWaypoint, setDraggableWaypoint] = useState<mapboxgl.Marker>();
    const [draggableWaypointOnMap, setDraggableWaypointOnMap] = useState(false);

    const onWaypointDragStart = useCallback(() => {
        if (interaction.type !== 'editingWaypoint') return;
        onInteractionChange((currentInteraction) => ({
            // The guard above does not correctly infer the type here
            ...(currentInteraction as EditingWaypointInteractionState),
            subtype: 'dragging',
        }));
    }, [interaction, onInteractionChange]);

    const onWaypointDragEnd = useCallback(() => {
        if (interaction.type !== 'editingWaypoint' || !draggableWaypoint) return;
        onInteractionChange((currentInteraction) => ({
            ...(currentInteraction as EditingWaypointInteractionState),
            subtype: 'normal',
            waypoint: {
                // The guard above does not correctly infer the type here
                ...(currentInteraction as EditingWaypointInteractionState).waypoint,
                geometry: {
                    type: 'Point',
                    coordinates: draggableWaypoint.getLngLat().toArray(),
                },
            },
        }));
    }, [interaction, onInteractionChange, draggableWaypoint]);

    // Create the Mapbox GL Marker on mount
    useEffect(() => {
        if (!markerRef.current) return;
        setDraggableWaypoint(new Marker({element: markerRef.current, draggable: true}));
    }, []);

    const latestInteraction = useLatest(interaction);

    // Add or remove the waypoint in response to changes in the interaction state
    useEffect(() => {
        if (!map) return;

        // Add the waypoint to the map and set the locaion when the interaction type changes
        if (
            latestInteraction.current.type === 'editingWaypoint' &&
            draggableWaypoint &&
            !draggableWaypointOnMap
        ) {
            draggableWaypoint.setLngLat(
                latestInteraction.current.waypoint.geometry.coordinates as mapboxgl.LngLatLike
            );
            draggableWaypoint.addTo(map);
            draggableWaypoint.on('dragstart', onWaypointDragStart);
            draggableWaypoint.on('dragend', onWaypointDragEnd);

            setDraggableWaypointOnMap(true);

            // Remove the waypoint from the map when the interaction type changes to normal
        } else if (
            latestInteraction.current.type !== 'editingWaypoint' &&
            draggableWaypoint &&
            draggableWaypointOnMap
        ) {
            draggableWaypoint.off('dragstart', onWaypointDragStart);
            draggableWaypoint.off('dragend', onWaypointDragEnd);
            draggableWaypoint.remove();
            setDraggableWaypointOnMap(false);
        }
    }, [
        // we don't want `interaction` as a dep, but `interaction.waypoint` is needed. However, we
        // pick up changes to it by watching `interaction.type`.
        latestInteraction,
        map,
        interaction.type,
        draggableWaypoint,
        setDraggableWaypointOnMap,
        draggableWaypointOnMap,
        onWaypointDragStart,
        onWaypointDragEnd,
    ]);

    const {properties: wpProps} =
        interaction.type === 'editingWaypoint' ? interaction.waypoint : ({properties: {}} as any);

    const markerType: MarkerType = isMarkerType(wpProps.marker_type) ? wpProps.marker_type : 'pin';
    const markerDecoration: string = wpProps.marker_decoration ?? '';
    const markerColor: GaiaColorHex = isGaiaColorHex(wpProps.marker_color)
        ? wpProps.marker_color
        : GaiaRed;

    const display =
        interaction.type === 'editingWaypoint' && !interaction.waypoint.properties.track_id
            ? 'grid'
            : 'none';

    return (
        <div
            ref={markerRef}
            style={{
                borderRadius: '50%',
                backgroundColor: 'rgba(45, 94, 56, 0.5)',
                border: `2px solid ${theme.palette.primary.main}`,
                height: '120px',
                width: '120px',
                position: 'relative',
                marginLeft: 0,
                top: -23,
                placeItems: 'center',
                boxSizing: 'border-box',
                display,
            }}
        >
            <WaypointIcon
                markerType={markerType}
                markerDecoration={markerDecoration}
                markerColor={markerColor}
                size='large'
            />
        </div>
    );
}
